<template>
  <div class="card-body table mb-0">

    <!-- HEADER -->
    <PageHeader v-if="showHeading" title="Stripe Events" class="poppins fw-normal text-nowrap mt-2" />

    <!-- EVENTS TABLE -->
    <GeneralTable :isProcessing="processing" :items="items" :fields="fields" :showEyeButton="true"
      :showActionButton="false" @showItem="openEventModal" />

    <!-- EVENT DETAILS MODAL -->
    <b-modal ref="eventModal" hide-footer size="xl" title="Event Details">
      <div v-if="selectedEvent" class="event-info">

        <!-- BASIC INFO -->
        <div class="event-column">
          <p><strong>Event ID:</strong> {{ selectedEvent.id }}</p>
          <p><strong>Type:</strong> {{ selectedEvent.type }}</p>
          <p><strong>Object:</strong> {{ selectedEvent.object }}</p>
          <p><strong>Created:</strong> {{ formatDate(selectedEvent.created) }}</p>
          <p><strong>API Version:</strong> {{ selectedEvent.api_version || "—" }}</p>
          <p><strong>Live Mode:</strong> {{ selectedEvent.livemode ? "Yes" : "No" }}</p>
        </div>

        <!-- SUMMARY -->
        <div class="event-column">
          <p><strong>Summary:</strong></p>
          <p>{{ eventSummary(selectedEvent) }}</p>
        </div>

        <!-- RELATED OBJECT -->
        <div class="event-column full-width">
          <h6>Related Object Details</h6>
          <pre class="json-view">
  {{ formatJson(selectedEvent.data?.object) }}
            </pre>
        </div>

        <!-- PREVIOUS ATTRIBUTES -->
        <div v-if="selectedEvent.data?.previous_attributes" class="event-column full-width">
          <h6>Previous Attributes</h6>
          <pre class="json-view">
  {{ formatJson(selectedEvent.data.previous_attributes) }}
            </pre>
        </div>

      </div>
    </b-modal>

  </div>
</template>

<script>
import GeneralTable from "@/components/MagicTable/GeneralTable.vue";
import PageHeader from "@/components/static/page-header";

export default {
  name: "UserStripeEvents",

  components: {
    GeneralTable,
    PageHeader
  },

  props: {
    user: {
      type: Object,
      required: true
    },
    showHeading: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      processing: false,
      items: [],
      selectedEvent: null,

      fields: [
        { key: "type", label: "Event Type" },
        { key: "summary", label: "Summary" },
        { key: "object_type", label: "Object" },
        { key: "created_at", label: "Date" },
        // { key: "action" }
      ]
    };
  },

  mounted() {
    this.fetchEvents();
  },

  methods: {
    /**
     * Fetch Stripe Events
     */
    fetchEvents() {
      this.processing = true;

      this.$axios
        .get("stripe-events/current-user-events", {
          params: { user_id: this.user.id }
        })
        .then(res => {
          const events = res?.data?.data?.events || [];

          this.items = events.map(evt => ({
            id: evt.id,
            type: evt.type,
            object_type: evt.data?.object?.object || "—",
            summary: this.eventSummary(evt),
            created_at: this.formatDate(evt.created),
            _raw: evt
          }));

          this.processing = false;
        })
        .catch(() => {
          this.processing = false;
        });
    },

    /**
     * Open modal
     */
    openEventModal(eventId) {
      const row = this.items.find(e => e.id === eventId);
      this.selectedEvent = row?._raw || null;
      this.$refs.eventModal.show();
    },


    eventSummary(event) {
      const type = event.type;
      const obj = event.data?.object || {};

      switch (type) {

        case "payment_method.attached": {
          if (obj.card?.last4) {
            return `A card payment method ending in ${obj.card.last4} was attached to customer ${obj.customer}`;
          }
          return "A payment method was attached to a customer";
        }

        case "customer.subscription.created":
          return "Customer subscribed to a plan";

        case "customer.subscription.deleted":
          return "Customer subscription was canceled";

        case "invoice.payment_succeeded": {
          const amount = obj.amount_paid
            ? `$${(obj.amount_paid / 100).toFixed(2)}`
            : "$0.00";
          return `Invoice payment for ${amount} was successful`;
        }

        case "invoice.paid": {
          const amount = obj.amount_paid
            ? `$${(obj.amount_paid / 100).toFixed(2)}`
            : "$0.00";
          return `Invoice for ${amount} was paid`;
        }

        case "invoice.finalized":
          return "A draft invoice was finalized";

        case "invoice.created":
          return "A draft invoice was created";

        case "charge.succeeded": {
          const amount = obj.amount
            ? `$${(obj.amount / 100).toFixed(2)}`
            : "";
          return `${obj.billing_details?.email || "Customer"} was charged ${amount}`;
        }

        case "payment_intent.succeeded": {
          const amount = obj.amount_received
            ? `$${(obj.amount_received / 100).toFixed(2)}`
            : "";
          return `The payment ${obj.id} for ${amount} has succeeded`;
        }

        case "setup_intent.succeeded":
          return "SetupIntent has succeeded";

        case "customer.updated":
          return "Customer details were updated";

        default:
          return type.replace(/\./g, " ");
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return "—";
      return new Date(timestamp * 1000).toLocaleString();
    },

    formatJson(obj) {
      if (!obj) return "—";
      return JSON.stringify(obj, null, 2);
    }
  }
};
</script>

<style scoped>
.event-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.event-column {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.full-width {
  grid-column: span 2;
}

.json-view {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  max-height: 400px;
  overflow: auto;
  font-size: 13px;
}

@media (max-width: 768px) {
  .event-info {
    grid-template-columns: 1fr;
  }

  .full-width {
    grid-column: span 1;
  }
}
</style>